import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, tooltipStyle } from '../types';

const RealtimeTab: React.FC<{ period: PeriodConfig }> = ({ period }) => {
  // Realtime always fetches live data regardless of period
  const { data, loading } = useAnalyticsData(
    ['overview', 'videos-overview', 'signups-hourly', 'recent-signups'],
    { type: 'live' },
  );

  if (loading) return <LoadingSpinner />;

  const overview = data['overview'];
  const videosOverview = data['videos-overview'];
  const recentSignups = data['recent-signups'] || [];
  const hourlySignups = (data['signups-hourly'] || []).map((d: any) => ({ ...d, hour: `${d.hour}:00` }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard title="Today's Signups" value={overview?.todaySignups || 0} subtitle="Users registered today" />
        <StatCard title="Today's Videos" value={videosOverview?.todayVideos || 0} subtitle="Videos uploaded today" />
        <StatCard title="Yesterday" value={overview?.yesterdaySignups || 0} subtitle="Signups yesterday" />
      </div>

      <ChartCard title="Signups by Hour (Today)">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={hourlySignups}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="hour" stroke="#64748b" fontSize={11} tickLine={false} />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#FF9C15" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Recent Signups">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-white/10">
                <th className="pb-3 font-medium">User</th>
                <th className="pb-3 font-medium">Country</th>
                <th className="pb-3 font-medium">Platform</th>
                <th className="pb-3 font-medium">Source</th>
                <th className="pb-3 font-medium">Verified</th>
                <th className="pb-3 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {recentSignups.map((user: any, i: number) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      {user.ProfileImageURL ? (
                        <img src={user.ProfileImageURL} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF9C15] to-[#F42026] flex items-center justify-center text-white text-xs font-bold">
                          {(user.UserName || user.Name || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium">{user.Name || user.UserName || 'Anonymous'}</p>
                        <p className="text-gray-500 text-xs">@{user.UserName || 'n/a'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 text-gray-300">{user.Location || '-'}</td>
                  <td className="py-3 text-gray-300">{user.RegistrationDevice || '-'}</td>
                  <td className="py-3 text-gray-300">{user.RegistrationType || '-'}</td>
                  <td className="py-3">
                    {user.IsVerified ? (
                      <span className="text-emerald-400 text-xs bg-emerald-500/10 px-2 py-0.5 rounded-full">Yes</span>
                    ) : (
                      <span className="text-gray-500 text-xs">No</span>
                    )}
                  </td>
                  <td className="py-3 text-gray-400 text-xs">{new Date(user.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
};

export default RealtimeTab;
